import { useEffect, useMemo, useState } from "react";
import RightTimeApi from "@/API";
import "@style/Admin.scss";
import Enums from "@/enums";
import CompanyDataModal from "@modals/CompanyDataModal";
import CompanyContentModal from "@modals/CompanyContentModal";
import ImagePickerModal from "@modals/ImagePickerModal";
import { AiOutlinePicture } from "react-icons/ai";
import { MdCheckBoxOutlineBlank, MdCheckBox } from "react-icons/md";

interface CompanyInfoTabProps {
  tabData?: any;
}

const CompanyInfoTab = (props: CompanyInfoTabProps) => {
  if (!props || !props.tabData) {
    console.error('CompanyInfoTab: props or props.tabData is undefined');
    return null;
  }

  const { company, updateSpinner, activeTab, adminPerms } = props.tabData;

  const [localCompany, updateLocalCompany] = useState<any>({});
  const [companyEditable, updateCompanyEditable] = useState(false);
  const [isCompanyDataModalOpen, setIsCompanyDataModalOpen] = useState(false);
  const [isCompanyContentModalOpen, setIsCompanyContentModalOpen] = useState(false);
  const [companyContentTypeId, setCompanyContentTypeId] = useState<number | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [companyImageUrl, updateCompanyImageUrl] = useState("");
  const isOnThisTab = props?.tabData?.activeTab === "company";

  useEffect(() => {
    if (isOnThisTab && company) {
      updateLocalCompany(company);
      refreshCompanyInfoAsync();
      if (company.imageId) {
        let url = RightTimeApi.URL("/Image/" + company.imageId);
        updateCompanyImageUrl(url);
      }
      if (company.permissions && company.permissions.length > 0) {
        const canEdit = company.permissions.some((p: any) => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.EditCompany) ||
          company.permissions.some((p: any) => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin) ||
          company.permissions.some((p: any) => p.companyId === company.id && p.permissionId === Enums.ROLE_PERMS.CompanyAdmin);
        updateCompanyEditable(canEdit);
        console.log("CompanyInfoTab: User can edit company:", canEdit);
      }
    }
  }, [props?.tabData?.activeTab, company]);

  async function refreshCompanyInfoAsync() {
    if (!company?.id) return;
    if (updateSpinner) updateSpinner(true);
    const resp = await RightTimeApi.getAsync("/CompanyInfo2/" + company.id);
    if (updateSpinner) updateSpinner(false);
    if (resp && resp.ok && resp.jsonData) {
      updateLocalCompany(resp.jsonData);
      if (resp.jsonData.imageId) {
        let url = RightTimeApi.URL("/Image/" + resp.jsonData.imageId);
        updateCompanyImageUrl(url);
      } else {
        updateCompanyImageUrl("");
      }
      if (resp.jsonData.permissions && resp.jsonData.permissions.length > 0) {
        const canEdit = resp.jsonData.permissions.some((p: any) => p.companyId === resp.jsonData.id && p.permissionId === Enums.ROLE_PERMS.EditCompany) ||
          resp.jsonData.permissions.some((p: any) => p.companyId === 1 && p.permissionId === Enums.ROLE_PERMS.SystemAdmin) ||
          resp.jsonData.permissions.some((p: any) => p.companyId === resp.jsonData.id && p.permissionId === Enums.ROLE_PERMS.CompanyAdmin);
        updateCompanyEditable(canEdit);
      }
    }
  }


  const defaultRoleName = useMemo(() => {
    if (!localCompany.roles || !localCompany.defaultUserRoleId) return "None";
    const role = localCompany.roles.find((r: any) => r.key === localCompany.defaultUserRoleId);
    return role?.value || "None";
  }, [localCompany.roles, localCompany.defaultUserRoleId]);

  const timedOutStatusName = useMemo(() => {
    if (!localCompany.statuses || !localCompany.timedOutStatusId) return "None";
    const status = localCompany.statuses.find((s: any) => s.key === localCompany.timedOutStatusId);
    return status?.value || "None";
  }, [localCompany.statuses, localCompany.timedOutStatusId]);

  const colorSchemeName = useMemo(() => {
    if (localCompany.colorScheme?.schemeName) return localCompany.colorScheme.schemeName;
    return localCompany.colorSchemeName || "";
  }, [localCompany.colorScheme, localCompany.colorSchemeName]);

  function openCompanyContentModal(contentTypeId?: number) {
    setCompanyContentTypeId(contentTypeId || null);
    setIsCompanyContentModalOpen(true);
  }

  if (!isOnThisTab) return null;


  // console.log(["colorSchemeId",localCompany.colorScheme]);
  if (isOnThisTab && localCompany) {
    return (
      <div className="tableWrapper dimensionTable inline-block companyDataTable">
        <table className="formTable">
          <caption>
            <span className="title">Company Data</span>
          </caption>
          <tbody>
            <tr>
              <th className="label">Name</th>
              <td>
                <span>{localCompany.name || ""}</span>
              </td>
            </tr>
            <tr>
              <th className="label">Alias</th>
              <td>
                <span>{localCompany.alias || ""}</span>
              </td>
            </tr>
            <tr>
              <th className="label">Billing Org</th>
              <td>
                <span>{localCompany.billingOrgName || ""}</span>
              </td>
            </tr>
            <tr>
              <td colSpan={2}>
                {company.imageId && companyImageUrl ? (
                  <img
                    className="logo company centered"
                    src={companyImageUrl}
                    alt=""
                    onClick={
                      companyEditable ? () => setIsImageModalOpen(true) : undefined
                    }
                    style={{ cursor: companyEditable ? "pointer" : "default" }}
                  />): <AiOutlinePicture />}
              </td>
            </tr>
            <tr
              className={companyEditable ? "contentSelectionRow" : undefined}
              onClick={companyEditable ? () => openCompanyContentModal(Enums.CONTENT_TYPE.RequestTableTitle) : undefined}
              title={companyEditable ? "Edit request table title content" : undefined}
            >
              <th className="label">Request Table Title</th>
              <td>
                <span className={localCompany.requestTableTitleContentText ? "" : "defaultSectionContentName"}>
                  {localCompany.requestTableTitleContentText || "Request"}
                </span>
              </td>
            </tr>
            <tr
              className={companyEditable ? "contentSelectionRow" : undefined}
              onClick={companyEditable ? () => openCompanyContentModal(Enums.CONTENT_TYPE.RequestModalTitle) : undefined}
              title={companyEditable ? "Edit request modal title content" : undefined}
            >
              <th className="label">Request Modal Title</th>
              <td>
                <span className={localCompany.requestModalTitleContentText ? "" : "defaultSectionContentName"}>
                  {localCompany.requestModalTitleContentText || "Create Test"}
                </span>
              </td>
            </tr>
            <tr>
              <th className="actions" colSpan={2}>
                {companyEditable ? (<>
                  <button className='editCompanyDataButton' onClick={() => setIsCompanyDataModalOpen(true)}>
                    Edit Company Data
                  </button>
                  {/* <button className='editCompanyContentButton' onClick={() => setIsCompanyContentModalOpen(true)}>
                    Edit Content
                  </button> */}
                  <button className='editCompanyImagesButton' onClick={() => setIsImageModalOpen(true)}>Edit Images</button>
                </>
                ) : null}
              </th>
            </tr>

          </tbody>
        </table>

        <CompanyDataModal
          isOpen={isCompanyDataModalOpen}
          onClose={async () => {
            setIsCompanyDataModalOpen(false);
            await refreshCompanyInfoAsync();
            if (typeof props.tabData?.refreshCompany === "function") {
              await props.tabData.refreshCompany();
            }
          }}
          companyId={company?.id}
          updateSpinner={updateSpinner}
          adminPerms={adminPerms}
        />

        <ImagePickerModal
          isOpen={isImageModalOpen}
          onClose={() => {
            setIsImageModalOpen(false);
            refreshCompanyInfoAsync();
          }}
          companyId={company?.id}
          updateSpinner={updateSpinner}
        />

        <CompanyContentModal
          isOpen={isCompanyContentModalOpen}
          onClose={() => {
            setIsCompanyContentModalOpen(false);
            setCompanyContentTypeId(null);
            refreshCompanyInfoAsync();
          }}
          companyId={company?.id}
          updateSpinner={updateSpinner}
          contentTypeId={companyContentTypeId}
        />
      </div>
    );
  } else {
    return null;
  }
};

export default CompanyInfoTab;

